import { FormattedDate, FormattedDateTime, Tag, useFormatDate, useFormatDateTime } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FilterItem, FilterOperator, FilterableEntity, FilterableField } from "./filterTypes";

const StyledTag = styled(Tag)`
	margin: ${({ theme }) => theme.spacing(1)};
`;

interface Props {
	filterItem: FilterItem;
	removeFilterItem: (filterItem: FilterItem) => void;
	onClick: () => void;
}

export const ActiveFilterItem: FC<Props> = ({ filterItem, removeFilterItem, onClick }) => {
	const { t } = useTranslation();

	return (
		<StyledTag color="dark" onClose={() => removeFilterItem(filterItem)} onClick={onClick}>
			{t(`${filterItem.entity}.${filterItem.field}`)}
			<span>
				{` ${t(filterItem.operator)} `}
				<ValueResolver
					entity={filterItem.entity}
					field={filterItem.field}
					operator={filterItem.operator}
					value={filterItem.value}
				/>
			</span>
		</StyledTag>
	);
};

interface ResolverProps {
	entity: FilterableEntity;
	field: FilterableField;
	operator: FilterOperator;
	value: any;
}

const ValueResolver: FC<ResolverProps> = (props) => {
	switch (props.entity) {
		case "EVENT":
			return <EventResolver {...props} />;
		case "APPLICATION":
			return <ApplicationResolver {...props} />;
		default:
			return null;
	}
};

const EventResolver: FC<ResolverProps> = ({ field, operator, value }) => {
	const { t } = useTranslation();
	const formatDateTime = useFormatDateTime();

	switch (field) {
		case "START":
		case "END":
			return operator === "BETWEEN" ? (
				t("{{start}} and {{end}}", {
					start: formatDateTime(value.start),
					end: formatDateTime(value.end),
				})
			) : (
				<FormattedDateTime dateTime={value} />
			);
		case "FORMAT":
			return hasOptions(operator) ? value.map((o: string) => t(o)).join(", ") : t(value);
		case "EVENT_TYPE":
		case "REGION":
		case "TARGET_GROUP":
		case "EVENT_CATEGORY":
		case "ORGANIZER":
		case "ADDRESS_COUNTRY":
		case "LOCATION":
		case "PRIMARY_ORGANIZATIONAL_UNIT":
			return hasOptions(operator) ? value.map((o: any) => o.name).join(", ") : value.name;
		case "INDUSTRIES":
		case "ORGANIZATIONAL_UNITS":
		case "TAGS":
			return value.map((o: any) => o.name).join(", ");
		case "EVENT_PARTICIPATION_STATUS":
			return t(`eventParticipationStatus.${value}`);
		default:
			return null;
	}
};

const ApplicationResolver: FC<ResolverProps> = ({ field, operator, value }) => {
	const { t } = useTranslation();
	const formatDate = useFormatDate();

	switch (field) {
		case "SETUP_DATE":
		case "DELETION_DATE":
			return operator === "BETWEEN" ? (
				t("{{start}} and {{end}}", {
					start: formatDate(value.start),
					end: formatDate(value.end),
				})
			) : (
				<FormattedDate date={value} />
			);
		case "TYPE":
			return hasOptions(operator) ? value.map((o: string) => t(o)).join(", ") : t(value);
		default:
			return value;
	}
};

const hasOptions = (operator: FilterOperator): boolean => {
	return (
		operator === "IN" ||
		operator === "NOT_IN" ||
		operator === "NOT_IN_RELATION" ||
		operator === "IN_RELATION" ||
		operator === "NOT_IN_ADDRESSES_COUNTRY" ||
		operator === "IN_ADDRESSES_COUNTRY" ||
		operator === "NOT_IN_APPLICATION_TYPE" ||
		operator === "IN_APPLICATION_TYPE"
	);
};
